/** Load Calendly embed assets once; open scheduling popup (URL from server config). */

const CALENDLY_JS = "https://assets.calendly.com/assets/external/widget.js";
const CALENDLY_CSS = "https://assets.calendly.com/assets/external/widget.css";

export type CalendlyPrefill = {
  name?: string;
  email?: string;
};

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (opts: { url: string; prefill?: CalendlyPrefill }) => void;
    };
  }
}

let loadPromise: Promise<void> | null = null;

function ensureCss(): void {
  if (document.querySelector(`link[href="${CALENDLY_CSS}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = CALENDLY_CSS;
  document.head.appendChild(link);
}

export function loadCalendlyWidget(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Calendly requires a browser."));
  }
  if (window.Calendly) return Promise.resolve();
  if (loadPromise) return loadPromise;

  ensureCss();
  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${CALENDLY_JS}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Calendly widget.")),
      );
      if (window.Calendly) resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = CALENDLY_JS;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Calendly widget."));
    document.body.appendChild(script);
  });
  return loadPromise;
}

export async function openCalendlyPopup(
  url: string,
  prefill?: CalendlyPrefill,
): Promise<void> {
  const trimmed = (url || "").trim();
  if (!trimmed) {
    throw new Error("Consultation booking URL is missing.");
  }
  await loadCalendlyWidget();
  if (!window.Calendly?.initPopupWidget) {
    throw new Error("Calendly widget is not available.");
  }
  const name = (prefill?.name || "").trim();
  const email = (prefill?.email || "").trim();
  const opts: { url: string; prefill?: CalendlyPrefill } = { url: trimmed };
  if (name || email) {
    opts.prefill = {
      ...(name ? { name } : {}),
      ...(email ? { email } : {}),
    };
  }
  window.Calendly.initPopupWidget(opts);
}
